import React, { useState, useMemo, useEffect } from 'react';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { ComplianceKeurmerkKaart } from '../components/ComplianceKeurmerkKaart';
import { ZijMenu, ZijMenuMobielKnop, PaginaKop } from '../components/ZijMenu';
import { PROFESSIONAL_COCKPIT_ITEMS } from '../components/ProfessionalWerkSchil';
import StatusDot from '../components/StatusDot';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { Card, Button, CreateInvoiceForm } from '../components';
import { useTranslation } from 'react-i18next';
import {
  FolderOpen,
  Search,
  Briefcase,
  Clock,
  ArrowRight,
  MessageSquare,
  DollarSign,
  Phone,
  Mail,
  Upload,
  Camera,
  Building,
  Plus,
  Download,
  X,
  Archive,
  RotateCcw,
  Eye,
} from 'lucide-react';
import api from '../services/api';
import { PROFESSIONAL_TYPE_LABELS as PROF_TYPE_LABELS } from '../types/professionalRates';
import { ProfessionalAgendaPage } from './ProfessionalAgendaPage';
import { ProfessionalReviewsPage } from './ProfessionalReviewsPage';
import { toast } from 'react-hot-toast';
import { uploadUrl } from '../utils/uploadUrl';

type CockpitView = 'agenda' | 'lopende_trajecten' | 'afgesloten_dossiers' | 'geplaatste_opdrachten' | 'opdrachtgevers' | 'financien' | 'reviews';

export const ProfessionalCockpitPage: React.FC = () => {
  const location = useLocation();
  const viewUitUrl = (search: string): CockpitView => {
    const params = new URLSearchParams(search);
    const v = params.get('view');
    const validViews: CockpitView[] = ['agenda', 'lopende_trajecten', 'afgesloten_dossiers', 'geplaatste_opdrachten', 'opdrachtgevers', 'financien', 'reviews'];
    // Oude deeplinks blijven werken: samengevoegde en verplaatste views wijzen door.
    const legacyViews: Record<string, CockpitView> = {
      uren: 'financien',
      facturen: 'financien',
      chat_opdrachtgever: 'opdrachtgevers',
      gesprekverslagen: 'lopende_trajecten',
      jaarverslagen: 'lopende_trajecten',
      notities: 'lopende_trajecten',
      actiepunten: 'lopende_trajecten',
      documenten: 'lopende_trajecten',
    };
    if (v && validViews.includes(v as CockpitView)) return v as CockpitView;
    if (v && legacyViews[v]) return legacyViews[v];
    return 'lopende_trajecten';
  };
  const [activeView, setActiveView] = useState<CockpitView>(() => viewUitUrl(location.search));
  // Volg de URL ook als de cockpit al open is (hoofdmenu-submenu of zoekvenster naar ?view=…)
  useEffect(() => {
    setActiveView(viewUitUrl(location.search));
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleViewChange = (view: CockpitView) => {
    setActiveView(view);
    navigate(`/professional/cockpit?view=${view}`, { replace: true });
  };

  const [editingDossierId, setEditingDossierId] = useState<string | null>(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showHoursForm, setShowHoursForm] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [clickingEmployerId, setClickingEmployerId] = useState<string | null>(null);
  const [selectedEmployer, setSelectedEmployer] = useState<any | null>(null);
  const [showCreateDossierModal, setShowCreateDossierModal] = useState(false);
  const [createDossierForm, setCreateDossierForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    employer_id: '',
    description: '',
    is_anonymous: false,
  });
  const [closedDossiersSearch, setClosedDossiersSearch] = useState('');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation('professional');

  const STATUS_OPTIONS = [
    { value: 'nieuwe_aanmelding', label: t('status.nieuwe_aanmelding') },
    { value: 'intake_ingepland', label: t('status.intake_ingepland') },
    { value: 'intake_uitgevoerd', label: t('status.intake_uitgevoerd') },
    { value: 'eerste_gesprek_ingepland', label: t('status.eerste_gesprek_ingepland') },
    { value: 'eerste_gesprek_uitgevoerd', label: t('status.eerste_gesprek_uitgevoerd') },
    { value: 'tweede_gesprek_ingepland', label: t('status.tweede_gesprek_ingepland') },
    { value: 'tweede_gesprek_uitgevoerd', label: t('status.tweede_gesprek_uitgevoerd') },
    { value: 'derde_gesprek_ingepland', label: t('status.derde_gesprek_ingepland') },
    { value: 'derde_gesprek_uitgevoerd', label: t('status.derde_gesprek_uitgevoerd') },
    { value: 'vierde_gesprek_ingepland', label: t('status.vierde_gesprek_ingepland') },
    { value: 'vierde_gesprek_uitgevoerd', label: t('status.vierde_gesprek_uitgevoerd') },
    { value: 'vijfde_gesprek_ingepland', label: t('status.vijfde_gesprek_ingepland') },
    { value: 'vijfde_gesprek_uitgevoerd', label: t('status.vijfde_gesprek_uitgevoerd') },
    { value: 'lopend', label: t('status.lopend') },
    { value: 'eindgesprek', label: t('status.eindgesprek') },
    { value: 'afsluiten', label: t('status.afsluiten') },
    { value: 'archived', label: t('status.archived') },
    { value: 'on_hold', label: t('status.on_hold') }
  ];

  const getStatusDisplayLabel = (status: string): string => {
    const key = `status.${status}` as const;
    const result = t(key);
    return result !== key ? result : status;
  };

  // Get professional profile with photo
  const { data: profileData, refetch: refetchProfile } = useQuery({
    queryKey: ['professional-profile'],
    queryFn: async () => {
      const response = await api.get('/professional/profile');
      return response.data;
    }
  });

  // Upload profile photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('profile_photo', file);
      // Get current profile data to preserve other fields
      const currentProfile = profileData?.data || {};
      // Add all existing profile fields to formData
      Object.keys(currentProfile).forEach(key => {
        if (key !== 'profile_photo_path' && currentProfile[key] !== null && currentProfile[key] !== undefined) {
          if (Array.isArray(currentProfile[key])) {
            formData.append(key, JSON.stringify(currentProfile[key]));
          } else if (typeof currentProfile[key] === 'boolean') {
            formData.append(key, currentProfile[key].toString());
          } else {
            formData.append(key, currentProfile[key]);
          }
        }
      });
      const response = await api.post('/professional/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('toast.photoUploaded'));
      refetchProfile();
      setShowPhotoUpload(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('toast.photoError'));
    }
  });

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('toast.fileTooLarge'));
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(t('toast.onlyImages'));
        return;
      }
      uploadPhotoMutation.mutate(file);
    }
  };

  // Get screening data to get professional role
  const { data: screeningData } = useQuery({
    queryKey: ['screening-status'],
    queryFn: async () => {
      const response = await api.get('/screening/status');
      return response.data;
    },
  });

  // Get professional role
  const professionalRole = useMemo(() => {
    if (!screeningData?.data?.screening?.selected_professional_types) return null;
    try {
      const types = typeof screeningData.data.screening.selected_professional_types === 'string'
        ? JSON.parse(screeningData.data.screening.selected_professional_types)
        : screeningData.data.screening.selected_professional_types;

      return types.length > 0 ? (PROF_TYPE_LABELS[types[0] as keyof typeof PROF_TYPE_LABELS] || types[0]) : null;
    } catch {
      return null;
    }
  }, [screeningData]);

  const isVertrouwenspersoon = useMemo(() => {
    if (!screeningData?.data?.screening?.selected_professional_types) return false;
    try {
      const types = typeof screeningData.data.screening.selected_professional_types === 'string'
        ? JSON.parse(screeningData.data.screening.selected_professional_types)
        : screeningData.data.screening.selected_professional_types;
      return Array.isArray(types) && types.includes('vertrouwenspersoon');
    } catch { return false; }
  }, [screeningData]);

  const createDossierMutation = useMutation({
    mutationFn: async (data: typeof createDossierForm) => {
      const payload: any = {
        client_name: data.client_name,
        is_anonymous: data.is_anonymous,
        visible_to_employer: false,
      };
      if (data.client_email) payload.client_email = data.client_email;
      if (data.client_phone) payload.client_phone = data.client_phone;
      if (data.employer_id) payload.employer_id = data.employer_id;
      if (data.description) payload.description = data.description;
      const res = await api.post('/professional/dossiers/vertrouwenspersoon', payload);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-dossiers'] });
      setShowCreateDossierModal(false);
      setCreateDossierForm({ client_name: '', client_email: '', client_phone: '', employer_id: '', description: '', is_anonymous: false });
      toast.success('Cliënt dossier aangemaakt');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Aanmaken mislukt');
    }
  });

  // Get dossiers (lopende trajecten - alleen actieve, niet afgesloten)
  const { data: dossiersRaw, isLoading: dossiersLoading } = useQuery({
    queryKey: ['professional-dossiers', 'active'],
    queryFn: async () => {
      const response = await api.get('/professional/dossiers', { params: { status: 'active' } });
      return response.data.data || [];
    }
  });

  // Get afgesloten dossiers (voor archief view)
  const { data: closedDossiersRaw, isLoading: closedDossiersLoading } = useQuery({
    queryKey: ['professional-dossiers', 'closed', closedDossiersSearch],
    queryFn: async () => {
      const params: Record<string, string> = { status: 'closed' };
      if (closedDossiersSearch.trim()) params.search = closedDossiersSearch.trim();
      const response = await api.get('/professional/dossiers', { params });
      return response.data.data || [];
    },
    enabled: activeView === 'afgesloten_dossiers'
  });

  const { data: geplaatsteOpdrachten = [], isLoading: loadingOpdrachten } = useQuery({
    queryKey: ['professional-vacancies'],
    queryFn: async () => {
      const res = await api.get('/professional/vacancies');
      return res.data?.data || [];
    },
    enabled: activeView === 'geplaatste_opdrachten',
    staleTime: 2 * 60 * 1000,
  });

  // Sort dossiers by datum acceptatie: nieuwste bovenaan (meest actueel), langstlopende onderaan
  const dossiers = useMemo(() => {
    if (!dossiersRaw) return [];
    return [...dossiersRaw].sort((a: any, b: any) => {
      const dateA = a.professional_accepted_at ? new Date(a.professional_accepted_at).getTime() : 0;
      const dateB = b.professional_accepted_at ? new Date(b.professional_accepted_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [dossiersRaw]);

  // Sort closed dossiers alphabetically by client name
  const closedDossiers = useMemo(() => {
    if (!closedDossiersRaw) return [];
    return [...closedDossiersRaw].sort((a: any, b: any) => {
      const nameA = (a.client_name || '').toLowerCase();
      const nameB = (b.client_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [closedDossiersRaw]);

  // Get active employers for chat
  const { data: employers } = useQuery({
    queryKey: ['active-employers'],
    queryFn: async () => {
      const response = await api.get('/professional/active-employers');
      return response.data.data || [];
    }
  });

  // Bedrijfspools waarin de professional geactiveerd is, met vast pooltarief.
  // De respons draagt ook de eigen beschikbaarheidsstatus (dubbele status-grendel).
  const { data: poolsResponse, isLoading: poolsLoading } = useQuery({
    queryKey: ['professional-pools'],
    queryFn: async () => {
      const response = await api.get('/professional/pools');
      return response.data;
    }
  });
  const pools = poolsResponse?.data || [];
  const beschikbaarheid = poolsResponse?.beschikbaarheid || null;





  // Get hours registrations - Always fetch
  const { data: hoursRegistrations, isLoading: hoursLoading } = useQuery({
    queryKey: ['hours-registrations-recent'],
    queryFn: async () => {
      const response = await api.get('/professional/hours', {
        params: { limit: 20 }
      });
      return response.data.data || [];
    }
  });

  // Get invoices - Always fetch
  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['professional-invoices'],
    queryFn: async () => {
      const response = await api.get('/invoices/professional');
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
  });


  // Update dossier status
  const updateDossier = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.patch(`/professional/dossiers/${id}`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.data?.status === 'archived') {
        toast.success(t('toast.dossierClosed'));
      } else if (variables.data?.status === 'lopend') {
        toast.success(t('toast.dossierReopened'));
      } else {
        toast.success(t('toast.statusUpdated'));
      }
      queryClient.invalidateQueries({ queryKey: ['professional-dossiers'] });
      setEditingDossierId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('toast.statusError'));
    }
  });

  const handleStatusChange = (dossierId: string, newStatus: string) => {
    updateDossier.mutate({ id: dossierId, data: { status: newStatus } });
  };

  const handleDossierClick = (dossierId: string) => {
    navigate(`/professional/dossiers/${dossierId}`);
  };

  const handleCloseDossier = (dossierId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    updateDossier.mutate({ id: dossierId, data: { status: 'archived' } });
  };

  const handleReopenDossier = (dossierId: string) => {
    updateDossier.mutate({ id: dossierId, data: { status: 'lopend' } });
  };





  // Create hours registration mutation
  const createHoursMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/professional/hours', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('toast.hoursRegistered'));
      queryClient.invalidateQueries({ queryKey: ['hours-registrations-recent'] });
      queryClient.invalidateQueries({ queryKey: ['hours-registrations'] });
      setShowHoursForm(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('toast.hoursRegisterError'));
    }
  });

  const profilePhoto = profileData?.data?.profile_photo_path
    ? uploadUrl(profileData.data.profile_photo_path)
    : null;

  /** Opent (of hergebruikt) de anonieme chat met de HR-contactpersoon van een opdrachtgever. */
  const openEmployerChat = async (employerId: string) => {
    if (!employerId) {
      toast.error(t('cockpit.noEmployerId'));
      return;
    }
    if (clickingEmployerId) return;

    setClickingEmployerId(employerId);
    try {
      const response = await api.post('/communication/conversations/professional-employer', {
        employer_id: employerId
      });
      const { success, data } = response.data || {};
      const conversationId = data?.id;

      if (success && conversationId) {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        navigate(`/berichten?conversation=${conversationId}`);
      } else {
        toast.error(t('cockpit.invalidServerResponse'));
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message || error?.message || t('toast.chatError');
      if (status === 409 || status === 400) {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        navigate('/berichten');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setClickingEmployerId(null);
    }
  };

  // Volgorde zoals Excel: trajecten bovenaan (gedeeld met ProfessionalWerkSchil)
  const sidebarItems = PROFESSIONAL_COCKPIT_ITEMS.map((item) => ({
    id: item.id,
    label: t(item.labelKey, { defaultValue: item.labelDefault }),
    description: item.description,
    icon: item.icon,
    ...('href' in item ? { href: item.href } : {}),
  }));

  const renderContent = () => {
    switch (activeView) {
      case 'lopende_trajecten':
  return (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <span className="text-sm text-gray-500">{t('cockpit.sortByAcceptance')}</span>
              <div className="flex gap-2 flex-wrap">
                {isVertrouwenspersoon && (
                  <Button onClick={() => setShowCreateDossierModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuw cliënt dossier
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleViewChange('afgesloten_dossiers')}>
                  <Archive className="w-4 h-4 mr-2" />
                  {t('cockpit.toClosedDossiers')}
                </Button>
              </div>
      </div>

            {dossiersLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">{t('common:common.loading')}</p>
              </div>
            ) : dossiers && dossiers.length > 0 ? (
              <>
                {/* Mobile cards */}
                <div className="space-y-3 md:hidden">
                  {dossiers.map((dossier: any) => {
                    const isEditing = editingDossierId === dossier.id;
                    const statusLabel = getStatusDisplayLabel(dossier.status);
                    return (
                      <Card
                        key={dossier.id}
                        className={`p-4 cursor-pointer ${isEditing ? 'bg-blue-50' : ''}`}
                        onClick={() => handleDossierClick(dossier.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">{dossier.client_name}</h3>
                            <p className="text-sm text-gray-600 truncate">
                              {dossier.employer_company || `${dossier.employer_first_name} ${dossier.employer_last_name}`}
                            </p>
                          </div>
                          {isEditing ? (
                            <select
                              className="text-sm border border-gray-300 rounded-md px-2 py-1 ml-2"
                              value={dossier.status}
                              onChange={(e) => { e.stopPropagation(); handleStatusChange(dossier.id, e.target.value); }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ml-2 ${
                              dossier.status === 'lopend' ? 'bg-blue-100 text-blue-800' :
                              dossier.status === 'afsluiten' ? 'bg-gray-100 text-gray-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {statusLabel}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          {(dossier.professional_role || professionalRole) && (
                            <p>{t('table.professionalRole')}: {dossier.professional_role || professionalRole}</p>
                          )}
                          {dossier.client_phone && (
                            <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {dossier.client_phone}</p>
                          )}
                          {dossier.client_email && (
                            <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {dossier.client_email}</p>
                          )}
                        </div>
                        <div className="mt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleCloseDossier(dossier.id, e)}
                            className="text-gray-600 hover:text-red-600"
                          >
                            <Archive className="w-4 h-4 mr-1" />
                            {t('cockpit.close')}
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
                {/* Desktop table */}
                <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.dossier')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.employerName')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.professionalRole')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.clientName')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.clientMobile')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.clientEmail')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.trajectStatus')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dossiers.map((dossier: any, index: number) => {
                      const isEditing = editingDossierId === dossier.id;
                      const statusLabel = getStatusDisplayLabel(dossier.status);

                      return (
                        <tr
                          key={dossier.id}
                          className={`hover:bg-gray-50 cursor-pointer ${isEditing ? 'bg-blue-50' : ''}`}
                          onClick={() => handleDossierClick(dossier.id)}
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.employer_company || `${dossier.employer_first_name} ${dossier.employer_last_name}`}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.professional_role || professionalRole || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.client_name}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.client_phone || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.client_email || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {isEditing ? (
                              <select
                                className="text-sm border border-gray-300 rounded-md px-2 py-1"
                                value={dossier.status}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(dossier.id, e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {STATUS_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                dossier.status === 'lopend' ? 'bg-blue-100 text-blue-800' :
                                dossier.status === 'afsluiten' ? 'bg-gray-100 text-gray-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {statusLabel}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleCloseDossier(dossier.id, e)}
                              className="text-gray-600 hover:text-red-600"
                            >
                              <Archive className="w-4 h-4 mr-1" />
                              {t('cockpit.close')}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
          </div>
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">{t('empty.noActiveTrajects')}</p>
                {isVertrouwenspersoon && (
                  <Button className="mt-4" onClick={() => setShowCreateDossierModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuw cliënt dossier aanmaken
                  </Button>
                )}
              </div>
            )}

            {/* Vertrouwenspersoon: Nieuw cliënt dossier modal */}
            {showCreateDossierModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                  <div className="flex justify-between items-center p-6 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">Nieuw cliënt dossier</h3>
                    <button onClick={() => setShowCreateDossierModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={(e) => { e.preventDefault(); createDossierMutation.mutate(createDossierForm); }} className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Naam cliënt <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        value={createDossierForm.client_name}
                        onChange={(e) => setCreateDossierForm(prev => ({ ...prev, client_name: e.target.value }))}
                        placeholder="Volledige naam van de cliënt"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">E-mailadres</label>
                        <input
                          type="email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          value={createDossierForm.client_email}
                          onChange={(e) => setCreateDossierForm(prev => ({ ...prev, client_email: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Telefoonnummer</label>
                        <input
                          type="tel"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          value={createDossierForm.client_phone}
                          onChange={(e) => setCreateDossierForm(prev => ({ ...prev, client_phone: e.target.value }))}
                        />
                      </div>
                    </div>
                    {employers && employers.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Opdrachtgever (optioneel)</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          value={createDossierForm.employer_id}
                          onChange={(e) => setCreateDossierForm(prev => ({ ...prev, employer_id: e.target.value }))}
                        >
                          <option value="">— Geen opdrachtgever —</option>
                          {employers.map((emp: any) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.company_name || `${emp.first_name} ${emp.last_name}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Omschrijving</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={3}
                        value={createDossierForm.description}
                        onChange={(e) => setCreateDossierForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Reden voor begeleiding (optioneel)"
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createDossierForm.is_anonymous}
                        onChange={(e) => setCreateDossierForm(prev => ({ ...prev, is_anonymous: e.target.checked }))}
                      />
                      <span className="text-sm text-gray-700">Anonieme melding (naam niet zichtbaar in rapportages)</span>
                    </label>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      Dit dossier is alleen zichtbaar voor jou als vertrouwenspersoon — niet voor de werkgever.
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setShowCreateDossierModal(false)}>Annuleren</Button>
                      <Button type="submit" loading={createDossierMutation.isPending}>
                        <Plus className="w-4 h-4 mr-2" />
                        Dossier aanmaken
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );

      case 'afgesloten_dossiers':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <Button variant="outline" onClick={() => handleViewChange('lopende_trajecten')}>
                <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                {t('cockpit.backToActiveTrajects')}
              </Button>
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t('cockpit.search')}</h3>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('cockpit.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={closedDossiersSearch}
                  onChange={(e) => setClosedDossiersSearch(e.target.value)}
                />
              </div>
            </div>

            {closedDossiersLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">{t('common:common.loading')}</p>
              </div>
            ) : closedDossiers && closedDossiers.length > 0 ? (
              <>
                {/* Mobile cards */}
                <div className="space-y-3 md:hidden">
                  {closedDossiers.map((dossier: any) => {
                    const statusLabel = getStatusDisplayLabel(dossier.status);
                    return (
                      <Card key={dossier.id} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">{dossier.client_name}</h3>
                            <p className="text-sm text-gray-600 truncate">
                              {dossier.employer_company || `${dossier.employer_first_name} ${dossier.employer_last_name}`}
                            </p>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 whitespace-nowrap ml-2">
                            {statusLabel}
                          </span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => handleDossierClick(dossier.id)} className="flex-1">
                            <Eye className="w-4 h-4 mr-1" />
                            {t('cockpit.view')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReopenDossier(dossier.id)} className="flex-1 text-blue-600 hover:text-blue-700">
                            <RotateCcw className="w-4 h-4 mr-1" />
                            {t('cockpit.reopen')}
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
                {/* Desktop table */}
                <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.dossier')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.employerName')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.clientName')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {closedDossiers.map((dossier: any, index: number) => {
                      const statusLabel = getStatusDisplayLabel(dossier.status);
                      return (
                        <tr key={dossier.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.employer_company || `${dossier.employer_first_name} ${dossier.employer_last_name}`}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {dossier.client_name}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDossierClick(dossier.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                {t('cockpit.view')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReopenDossier(dossier.id)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                {t('cockpit.reopen')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Archive className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">
                  {closedDossiersSearch.trim()
                    ? t('empty.noSearchResults')
                    : t('empty.noClosedDossiers')}
                </p>
              </div>
            )}
          </div>
        );

      case 'opdrachtgevers':
        return (
          <div className="space-y-4">
            {/* Eigen status (dubbele status-grendel): intern bolletje + externe vindbaarheid */}
            {beschikbaarheid && (
              <Card className="p-4">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Status in je pools:</span>
                    <StatusDot
                      status={beschikbaarheid.pool_status}
                      beschikbaarVanaf={beschikbaarheid.pool_beschikbaar_vanaf}
                      withLabel
                    />
                  </div>
                  <div className="text-sm text-gray-700">
                    {beschikbaarheid.extern_vindbaar ? (
                      <span>Vindbaar voor nieuwe opdrachtgevers ({beschikbaarheid.lopende_trajecten}/{beschikbaarheid.max_actieve_trajecten} trajecten)</span>
                    ) : (
                      <span className="text-red-700">
                        Onzichtbaar voor nieuwe opdrachtgevers
                        {beschikbaarheid.extern_reden === 'compliance_hold'
                          ? ' — compliance-hold: upload je nieuwe documenten'
                          : beschikbaarheid.extern_reden === 'clientenstop'
                          ? ' — cliëntenstop actief'
                          : ` — capaciteit bereikt (${beschikbaarheid.lopende_trajecten}/${beschikbaarheid.max_actieve_trajecten})`}
                      </span>
                    )}
                  </div>
                </div>
                {beschikbaarheid.pool_status === 'ROOD' && (
                  <p className="text-xs text-gray-500 mt-2">
                    Geen open tijdslot in de komende 14 dagen — open een slot in je agenda en je springt direct weer op groen.
                  </p>
                )}
              </Card>
            )}

            {/* Pools & tarieven — waar ben ik geactiveerd en tegen welk tarief */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Mijn pools & tarieven</h2>
              {poolsLoading ? (
                <div className="text-center py-6 text-gray-500">{t('common:common.loading')}</div>
              ) : pools && pools.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pools.map((pool: any) => (
                    <Card key={pool.connection_id} className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{pool.employer_name}</h3>
                          <p className="text-sm text-gray-600 truncate">
                            {pool.pool_label || 'Nog geen pool toegewezen'}
                          </p>
                        </div>
                        {pool.agreement_approved ? (
                          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs whitespace-nowrap">
                            Actief
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs whitespace-nowrap">
                            Niet getekend
                          </span>
                        )}
                      </div>

                      <div className="mt-3 flex items-baseline gap-2">
                        <span className="text-2xl font-semibold text-gray-900">
                          {pool.hourly_rate != null ? `€${pool.hourly_rate.toFixed(2)}` : '—'}
                        </span>
                        <span className="text-sm text-gray-500">per uur</span>
                      </div>
                      {pool.rate_source === 'profiel' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Profieltarief — nog geen getekend pooltarief
                        </p>
                      )}
                      {pool.discount_percent > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Incl. {pool.discount_percent}% staffelkorting
                        </p>
                      )}
                      {pool.rate_at_floor && (
                        <p className="text-xs text-amber-700 mt-1">
                          Opgetrokken naar de Wet DBA-ondergrens van €{pool.rate_floor}
                        </p>
                      )}

                      <dl className="mt-3 space-y-1 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <dt>Lopende dossiers</dt>
                          <dd className="text-gray-900">{pool.active_dossiers}</dd>
                        </div>
                        {pool.intake_minutes && (
                          <div className="flex justify-between">
                            <dt>Intake / vervolg</dt>
                            <dd className="text-gray-900">
                              {pool.intake_minutes} / {pool.followup_minutes ?? '—'} min
                            </dd>
                          </div>
                        )}
                        {pool.max_sessions && (
                          <div className="flex justify-between">
                            <dt>Sessieplafond</dt>
                            <dd className="text-gray-900">{pool.max_sessions}</dd>
                          </div>
                        )}
                        {pool.anonymous_billing && (
                          <div className="flex justify-between">
                            <dt>Facturatie</dt>
                            <dd className="text-gray-900">Anoniem (meldingscode)</dd>
                          </div>
                        )}
                      </dl>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Building className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                  <p className="text-gray-600">Je bent nog in geen enkele bedrijfspool geactiveerd.</p>
                </div>
              )}
            </section>

            <h2 className="text-lg font-semibold text-gray-900 pt-2">Opdrachtgevers</h2>

            {selectedEmployer && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedEmployer(null)}>
                <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedEmployer.employer_company_name || `${selectedEmployer.employer_first_name} ${selectedEmployer.employer_last_name}`}
                    </h3>
                    <button type="button" onClick={() => setSelectedEmployer(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-3 text-sm">
                    {selectedEmployer.employer_company_name && (
                      <div><span className="font-medium text-gray-700">{t('dossierDetail.companyName')}:</span> <span className="text-gray-900">{selectedEmployer.employer_company_name}</span></div>
                    )}
                    {(selectedEmployer.employer_first_name || selectedEmployer.employer_last_name) && (
                      <div><span className="font-medium text-gray-700">{t('dossierDetail.contactPerson')}:</span> <span className="text-gray-900">{selectedEmployer.employer_first_name} {selectedEmployer.employer_last_name}</span></div>
                    )}
                    {selectedEmployer.employer_email && (
                      <div><span className="font-medium text-gray-700">{t('dossierDetail.email')}:</span> <a href={`mailto:${selectedEmployer.employer_email}`} className="text-blue-600 hover:underline">{selectedEmployer.employer_email}</a></div>
                    )}
                    {selectedEmployer.employer_phone && (
                      <div><span className="font-medium text-gray-700">{t('dossierDetail.phone')}:</span> <span className="text-gray-900">{selectedEmployer.employer_phone}</span></div>
                    )}
                    {selectedEmployer.employer_address && (
                      <div><span className="font-medium text-gray-700">{t('dossierDetail.address')}:</span> <span className="text-gray-900">{selectedEmployer.employer_address}</span></div>
                    )}
                    {selectedEmployer.employer_city && (
                      <div><span className="font-medium text-gray-700">{t('dossierDetail.city')}:</span> <span className="text-gray-900">{selectedEmployer.employer_city}</span></div>
                    )}
                    <div><span className="font-medium text-gray-700">Dossiers:</span> <span className="text-gray-900">{selectedEmployer.dossier_count || 0}</span></div>
                  </div>
                </div>
              </div>
            )}

            {employers && employers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {employers.map((employer: any) => {
                  const employerId = employer.employer_user_id || employer.employer_id || employer.id;
                  const isClicking = clickingEmployerId === employerId;
                  return (
                  <Card
                    key={employerId}
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedEmployer(employer)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <Building className="w-6 h-6 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">
                          {employer.employer_company_name || `${employer.employer_first_name} ${employer.employer_last_name}`}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {employer.dossier_count || 0} dossier(s)
                        </p>
                        {employer.employer_email && (
                          <p className="text-xs text-blue-600 mt-1 truncate">{employer.employer_email}</p>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full sm:w-auto"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            openEmployerChat(employerId);
                          }}
                          disabled={isClicking}
                          loading={isClicking}
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          {isClicking ? t('cockpit.openingChat') : t('cockpit.openChat')}
                        </Button>
                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">{t('empty.noActiveEmployers')}</p>
              </div>
            )}
          </div>
        );

      case 'financien':
        return (
          <div className="space-y-8">
            <Link
              to="/professional/profile?tab=facturen"
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-navy-800 hover:bg-gray-50"
            >
              <span>{t('cockpit.facturenProfielLink')}</span>
              <ArrowRight className="w-4 h-4 flex-shrink-0" />
            </Link>
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('cockpit.hoursRegistration')}</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <p className="text-sm text-gray-500">{t('cockpit.hoursFromAgendaHint')}</p>
              <Button variant="outline" onClick={() => setShowHoursForm(!showHoursForm)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('cockpit.manualHours')}
              </Button>
            </div>

            {showHoursForm && (
              <HoursRegistrationForm
                employers={employers || []}
                dossiers={dossiers || []}
                onSubmit={(data) => createHoursMutation.mutate(data)}
                onCancel={() => setShowHoursForm(false)}
              />
            )}
            {hoursLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('common:common.loading')}</p>
                      </div>
            ) : hoursRegistrations && hoursRegistrations.length > 0 ? (
              <>
                {/* Mobile cards */}
                <div className="space-y-3 md:hidden">
                  {hoursRegistrations.map((reg: any) => (
                    <Card key={reg.id} className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-medium text-gray-900 text-sm truncate flex-1">
                          {reg.dossier_client_name || reg.beschrijving || reg.omschrijving || '-'}
                          {reg.bron === 'agenda' && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] align-middle">
                              {t('cockpit.fromAgenda')}
                            </span>
                          )}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                          {new Date(reg.datum).toLocaleDateString(i18n.language)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>{reg.tijd_minuten ? `${(reg.tijd_minuten / 60).toFixed(2)} uur` : '-'}</span>
                        <span className="font-medium">{reg.total_amount ? `€${parseFloat(reg.total_amount).toFixed(2)}` : '-'}</span>
                      </div>
                    </Card>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.date')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.dossier')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.hours')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hoursRegistrations.map((reg: any) => (
                      <tr key={reg.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(reg.datum).toLocaleDateString(i18n.language)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {reg.dossier_client_name || reg.beschrijving || reg.omschrijving || '-'}
                          {reg.bron === 'agenda' && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                              {t('cockpit.fromAgenda')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {reg.tijd_minuten ? `${(reg.tijd_minuten / 60).toFixed(2)} uur` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {reg.total_amount ? `€${parseFloat(reg.total_amount).toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">{t('empty.noHoursRegistered')}</p>
              </div>
            )}
          </div>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('cockpit.invoices')}</h2>
          <div className="space-y-4">
            <div className="flex justify-end items-center mb-4">
              <Button onClick={() => setShowInvoiceForm(!showInvoiceForm)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('cockpit.newInvoice')}
              </Button>
            </div>

            {showInvoiceForm && (
              <CreateInvoiceForm
                employers={employers || []}
                onSuccess={() => setShowInvoiceForm(false)}
                onCancel={() => setShowInvoiceForm(false)}
              />
            )}

            {invoicesLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('common:common.loading')}</p>
              </div>
            ) : invoices && invoices.length > 0 ? (
              <>
                {/* Mobile cards */}
                <div className="space-y-3 md:hidden">
                  {invoices.map((invoice: any) => (
                    <Card key={invoice.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm">
                            {invoice.invoice_number || invoice.id}
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {invoice.employer_first_name} {invoice.employer_last_name}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ml-2 ${
                          invoice.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                          invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status === 'paid' ? t('cockpit.paid') :
                           invoice.status === 'pending' ? t('cockpit.open') : invoice.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{invoice.invoice_month}/{invoice.invoice_year}</span>
                        <span className="font-semibold text-gray-900">€{parseFloat(invoice.total_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            const pdfUrl = `/api/invoices/professional/${invoice.id}/pdf`;
                            window.open(pdfUrl, '_blank');
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {t('cockpit.download')}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.invoiceNumber')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.employer')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.period')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.amount')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoices.map((invoice: any) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {invoice.invoice_number || invoice.id}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {invoice.employer_first_name} {invoice.employer_last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {invoice.invoice_month}/{invoice.invoice_year}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          €{parseFloat(invoice.total_amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            invoice.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                            invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.status === 'paid' ? t('cockpit.paid') :
                             invoice.status === 'pending' ? t('cockpit.open') : invoice.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const pdfUrl = `/api/invoices/professional/${invoice.id}/pdf`;
                              window.open(pdfUrl, '_blank');
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {t('cockpit.download')}
                        </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                      </div>
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">{t('empty.noInvoices')}</p>
                    </div>
            )}
          </div>
            </section>
          </div>
        );

      case 'agenda':
        return <ProfessionalAgendaPage embedded />;

      case 'reviews':
        return <ProfessionalReviewsPage embedded />;

      case 'geplaatste_opdrachten':
        return (
          <div className="space-y-4">
            {loadingOpdrachten && (
              <div className="text-center py-12 text-gray-400">Laden...</div>
            )}
            {!loadingOpdrachten && geplaatsteOpdrachten.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Nog geen opdrachten beschikbaar</p>
                <p className="text-sm mt-1">Zodra opdrachtgevers een vacature plaatsen die past bij uw type, verschijnt die hier.</p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4">
              {geplaatsteOpdrachten.map((v: any) => {
                const tarifLabel = (() => {
                  if (v.tarief_type === 'in_overleg') return 'In overleg';
                  if (v.tarief_type === 'vanaf' && v.tarief_vanaf) return `Vanaf €${v.tarief_vanaf}/u`;
                  if (v.tarief_type === 'range' && v.tarief_vanaf && v.tarief_tot) return `€${v.tarief_vanaf} – €${v.tarief_tot}/u`;
                  return null;
                })();
                const urenLabel = (() => {
                  if (v.uren_per_week_type === 'fulltime') return 'Fulltime';
                  if (v.uren_per_week_type === 'parttime') return 'Parttime';
                  if (v.uren_per_week_type === 'range' && v.uren_per_week_min) return `${v.uren_per_week_min}–${v.uren_per_week_max || '?'} u/w`;
                  return 'Niet te voorspellen';
                })();
                const sluit = v.sluitingsdatum ? new Date(v.sluitingsdatum).toLocaleDateString('nl-NL') : null;
                return (
                  <div key={v.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{v.titel || v.professional_type}</h3>
                        <button
                          className="text-sm text-blue-600 hover:underline font-medium mt-0.5"
                          onClick={() => setSelectedEmployer(v)}
                        >
                          {v.bedrijfsnaam || v.employer_company_name || 'Onbekend bedrijf'}
                        </button>
                      </div>
                      {sluit && (
                        <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full shrink-0">
                          Sluit {sluit}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4 line-clamp-3">{v.beschrijving}</p>
                    <div className="flex flex-wrap gap-2 text-sm mb-4">
                      {v.provincie && <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">{v.provincie}</span>}
                      {v.branche && <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">{v.branche}</span>}
                      {tarifLabel && <span className="bg-green-50 text-green-700 px-2 py-1 rounded">{tarifLabel}</span>}
                      {urenLabel && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">{urenLabel}</span>}
                      {v.nobco_required && <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded">NOBCO vereist</span>}
                    </div>
                    {v.selectieprocedure && (
                      <p className="text-sm text-gray-500 mb-3">
                        <span className="font-medium">Procedure:</span> {
                          { direct: 'Direct beginnen bij akkoord', telefonisch: 'Eerst telefonisch contact', locatie: 'Gesprek op locatie', teams: 'Gesprek via Teams' }[v.selectieprocedure as string] || v.selectieprocedure
                        }
                      </p>
                    )}
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/professional/vacancies/${v.id}/reageer`, {});
                            toast.success('Uw interesse is doorgegeven aan de opdrachtgever');
                          } catch (err: any) {
                            toast.error(err?.response?.data?.message || 'Probeer het later opnieuw');
                          }
                        }}
                        className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Interesse tonen
                      </button>
                      <button
                        onClick={() => setSelectedEmployer(v)}
                        className="px-4 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:border-gray-400 transition-colors"
                      >
                        Bedrijfsprofiel
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Employer profile modal */}
            {selectedEmployer && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{selectedEmployer.bedrijfsnaam}</h3>
                    <button onClick={() => setSelectedEmployer(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    {selectedEmployer.branche && <p><span className="font-medium">Branche:</span> {selectedEmployer.branche}</p>}
                    {selectedEmployer.company_location && <p><span className="font-medium">Locatie:</span> {selectedEmployer.company_location}</p>}
                    {selectedEmployer.provincie && <p><span className="font-medium">Provincie:</span> {selectedEmployer.provincie}</p>}
                    {selectedEmployer.beschrijving && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="font-medium mb-1">Over de opdracht</p>
                        <p className="text-gray-600">{selectedEmployer.beschrijving}</p>
                      </div>
                    )}
                    {selectedEmployer.vereiste_vaardigheden && (
                      <div className="mt-2">
                        <p className="font-medium mb-1">Vereiste vaardigheden</p>
                        <p className="text-gray-600">{selectedEmployer.vereiste_vaardigheden}</p>
                      </div>
                    )}
                    {selectedEmployer.goed_om_te_weten && (
                      <div className="mt-2">
                        <p className="font-medium mb-1">Goed om te weten</p>
                        <p className="text-gray-600">{selectedEmployer.goed_om_te_weten}</p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedEmployer(null)}
                    className="mt-5 w-full py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Sluiten
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

                      return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="max-w-7xl mx-auto w-full flex flex-col flex-1">
      <ZijMenuMobielKnop onClick={() => setSidebarOpen(true)} />

      {/* Linkerkolom + inhoud in BeLagom-huisstijl (gedeeld ZijMenu) */}
      <div className="flex flex-1">
        <ZijMenu
          titel={t('cockpit.cockpitTitle', 'Mijn Cockpit')}
          ondertitel={t('cockpit.welcomeDesc')}
          kop={
            <div className="flex items-center gap-3 mb-4">
              <div className="relative group cursor-pointer flex-shrink-0" onClick={() => setShowPhotoUpload(!showPhotoUpload)}>
                <BelagomProfilePhoto src={profilePhoto} size={44} />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full flex items-center justify-center transition-opacity">
                  <Camera className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {showPhotoUpload && (
                  <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-20 min-w-[150px]" onClick={(e) => e.stopPropagation()}>
                    <label className="cursor-pointer flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 mb-2">
                      <Upload className="w-4 h-4" />
                      <span>{t('cockpit.uploadPhoto')}</span>
                      <input type="file" className="sr-only" accept="image/*" onChange={handlePhotoUpload} disabled={uploadPhotoMutation.isPending} />
                    </label>
                    <Link to="/professional/profile" className="block text-xs text-navy-600 hover:text-navy-800 mb-2" onClick={() => setShowPhotoUpload(false)}>
                      {t('cockpit.viaProfileEdit')}
                    </Link>
                    <button onClick={() => setShowPhotoUpload(false)} className="text-xs text-gray-500 hover:text-gray-700">
                      {t('common:common.cancel')}
                    </button>
                    {uploadPhotoMutation.isPending && <p className="text-xs text-gray-500 mt-2">{t('cockpit.uploading')}</p>}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy-900 leading-tight truncate">{user?.first_name} {user?.last_name}</p>
                {professionalRole && <p className="text-xs text-navy-500 leading-tight truncate">{professionalRole}</p>}
              </div>
            </div>
          }
          groepen={[{
            items: sidebarItems.map((item) => (item as any).href
              ? { key: item.id, label: item.label, omschrijving: item.description, icon: item.icon, href: (item as any).href }
              : { key: item.id, label: item.label, omschrijving: item.description, icon: item.icon, actief: activeView === item.id, onClick: () => handleViewChange(item.id as CockpitView) }),
          }]}
          mobielOpen={sidebarOpen}
          onMobielSluit={() => setSidebarOpen(false)}
        />

        {/* Content */}
        <div className="flex-1 min-w-0 bg-gray-50">
          <div className="p-4 sm:p-6 space-y-5">
            <PaginaKop
              titel={sidebarItems.find(i => i.id === activeView)?.label ?? t('cockpit.cockpitTitle', 'Mijn Cockpit')}
              ondertitel={sidebarItems.find(i => i.id === activeView)?.description ?? t('cockpit.welcomeDesc')}
            />
            <ComplianceKeurmerkKaart />
            {renderContent()}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};



// Hours Registration Form Component
const HoursRegistrationForm: React.FC<{
  employers: any[];
  dossiers: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ employers, dossiers, onSubmit, onCancel }) => {
  const { t } = useTranslation('professional');

  const HOUR_TYPES = [
    { value: 'intake_op_locatie', label: t('hourTypes.intake_locatie') },
    { value: 'bezoek_op_locatie', label: t('hourTypes.bezoek_locatie') },
    { value: 'online_intake', label: t('hourTypes.online_intake') },
    { value: 'online_gesprek', label: t('hourTypes.online_gesprek') },
    { value: 'communicatie', label: t('hourTypes.communicatie') },
    { value: 'administratie', label: t('hourTypes.administratie') },
    { value: 'overige', label: t('hourTypes.overige') }
  ];

  const [formData, setFormData] = useState({
    employer_id: '',
    dossier_id: '',
    datum: new Date().toISOString().split('T')[0],
    tijd_minuten: '',
    type: 'overige',
    omschrijving: '',
    aantal_km: '',
    reistijd_uren: '',
    parkeerkosten: '',
    parking_receipt_file_path: '',
    onkosten_locatie: '',
    afhuren_locatie: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      tijd_minuten: parseInt(formData.tijd_minuten),
      omschrijving: formData.omschrijving || undefined,
      aantal_km: formData.aantal_km ? parseFloat(formData.aantal_km) : undefined,
      reistijd_uren: formData.reistijd_uren ? parseFloat(formData.reistijd_uren) : undefined,
      parkeerkosten: formData.parkeerkosten ? parseFloat(formData.parkeerkosten) : undefined,
      parking_receipt_file_path: formData.parking_receipt_file_path || undefined,
      onkosten_locatie: formData.onkosten_locatie ? parseFloat(formData.onkosten_locatie) : undefined,
      afhuren_locatie: formData.afhuren_locatie ? parseFloat(formData.afhuren_locatie) : undefined,
      dossier_id: formData.dossier_id || undefined
    });
  };

  const filteredDossiers = dossiers.filter((d: any) => d.employer_id === formData.employer_id);

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('cockpit.newHoursRegistration')}</h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.employerRequired')}
            </label>
            <select
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.employer_id}
              onChange={(e) => setFormData({ ...formData, employer_id: e.target.value, dossier_id: '' })}
            >
              <option value="">{t('form.selectEmployer')}</option>
              {employers.map((emp: any) => (
                <option key={emp.employer_user_id || emp.employer_id} value={emp.employer_user_id || emp.employer_id}>
                  {emp.employer_company_name || `${emp.employer_first_name} ${emp.employer_last_name}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.dateRequired')}
            </label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.datum}
              onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.dossierOptional')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.dossier_id}
              onChange={(e) => setFormData({ ...formData, dossier_id: e.target.value })}
            >
              <option value="">{t('form.other')}</option>
              {filteredDossiers.map((d: any) => (
                <option key={d.id} value={d.id}>{d.client_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.typeRequired')}
            </label>
            <select
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              {HOUR_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.timeMinutes')}
            </label>
            <input
              type="number"
              required
              min="1"
              placeholder={t('form.timePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.tijd_minuten}
              onChange={(e) => setFormData({ ...formData, tijd_minuten: e.target.value })}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.descriptionOptional')}
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder={t('form.descriptionPlaceholder')}
              value={formData.omschrijving}
              onChange={(e) => setFormData({ ...formData, omschrijving: e.target.value })}
            />
          </div>
        </div>

        {/* Expense fields */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">{t('form.expenses')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.kmDriven')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={formData.aantal_km}
                onChange={(e) => setFormData({ ...formData, aantal_km: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.travelTime')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={formData.reistijd_uren}
                onChange={(e) => setFormData({ ...formData, reistijd_uren: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.parkingCosts')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={formData.parkeerkosten}
                onChange={(e) => setFormData({ ...formData, parkeerkosten: e.target.value })}
              />
              <label className="block text-sm font-medium text-gray-700 mb-2 mt-2">
                {t('form.parkingReceipt')}
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      const uploadFormData = new FormData();
                      uploadFormData.append('receipt', file);
                      const response = await api.post('/uploads/parking-receipt', uploadFormData, {
                        headers: {
                          'Content-Type': 'multipart/form-data'
                        }
                      });
                      if (response.data.success) {
                        setFormData(prev => ({ ...prev, parking_receipt_file_path: response.data.file_path }));
                        toast.success(t('toast.receiptUploaded'));
                      }
                    } catch (error: any) {
                      toast.error(error?.response?.data?.error || t('toast.receiptUploadError'));
                    }
                  }
                }}
              />
              {formData.parking_receipt_file_path && (
                <p className="text-sm text-blue-600 mt-1">{t('cockpit.receiptUploaded')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.locationExpenses')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={formData.onkosten_locatie}
                onChange={(e) => setFormData({ ...formData, onkosten_locatie: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.locationRental')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={formData.afhuren_locatie}
                onChange={(e) => setFormData({ ...formData, afhuren_locatie: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common:common.cancel')}
          </Button>
          <Button type="submit">
            {t('common:common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};
